import type { Card, Rank, Suit } from "./types.js";

// ============================================================
// Seeded PRNG: Mulberry32
// ============================================================
// Deterministisch: gleicher Seed → gleicher Shuffle.
// Seed wird in der DB gespeichert → jeder Spieler kann dasselbe
// Spiel anhand der ID reproduzieren.

function mulberry32(seed: number): () => number {
  let s = seed >>> 0; // Unsigned 32-bit sicherstellen
  return function () {
    s += 0x6d2b79f5;
    let z = s;
    z = Math.imul(z ^ (z >>> 15), z | 1);
    z ^= z + Math.imul(z ^ (z >>> 7), z | 61);
    return ((z ^ (z >>> 14)) >>> 0) / 4294967296;
  };
}

// ============================================================
// Farb-Mapping
// ============================================================

const SUIT_COLOR: Record<Suit, "red" | "black"> = {
  clubs: "black",
  spades: "black",
  hearts: "red",
  diamonds: "red",
};

const SUITS: readonly Suit[] = ["clubs", "diamonds", "hearts", "spades"];
const RANKS: readonly Rank[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];

// ============================================================
// Deck erstellen
// ============================================================

/** Erstellt ein geordnetes 52-Karten-Deck (Ass bis König, nach Farbe). */
export function createDeck(): Card[] {
  const deck: Card[] = [];
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      deck.push({
        id: `${suit}-${rank}`,
        suit,
        rank,
        color: SUIT_COLOR[suit],
        faceUp: false,
      });
    }
  }
  return deck;
}

// ============================================================
// Fisher-Yates Shuffle mit Seeded RNG
// ============================================================

/** Mischt eine Kopie des Decks mit dem gegebenen Seed. */
export function shuffleDeck(deck: readonly Card[], seed: number): Card[] {
  const shuffled = [...deck];
  const rng = mulberry32(seed);
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = shuffled[i];
    const src = shuffled[j];
    if (tmp !== undefined && src !== undefined) {
      shuffled[i] = src;
      shuffled[j] = tmp;
    }
  }
  return shuffled;
}

/** Erstellt ein neues Deck und mischt es mit dem gegebenen Seed. */
export function createShuffledDeck(seed: number): Card[] {
  return shuffleDeck(createDeck(), seed);
}

/** Generiert einen zufälligen Seed (für neue Spiele). */
export function generateSeed(): number {
  return Math.floor(Math.random() * 0xffffffff);
}
